import type { FC } from 'react';
import type {
    FieldValues,
    FieldArray,
    FieldArrayMethodProps,
    FieldArrayPath,
    FieldError,
    DeepMap,
} from 'react-hook-form';

import { FormInput } from '@catalyze/ui';
import type { TExpectedDecisionMaker } from 'api/types/edm';

import { Container } from './styled';

import { AddButton, RemoveButton } from '../InputFieldsArray/styled';

const MAX_FIELDS_LENGTH = 3; // maximum 3 phones/emails
const MIN_FIELDS_LENGTH = 1; // minimum 1 phone/email

// Inspired by https://react-hook-form.com/ts#FieldValues
export type FieldErrors<TFieldValues extends FieldValues = FieldValues> =
    DeepMap<TFieldValues, FieldError>;

interface IAddressInputFields<
    TFieldValues extends FieldValues = FieldValues,
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>
> {
    addresses: TExpectedDecisionMaker['addresses'];
    onRemoveField: (index: number) => void;
    onAddField: (
        value:
            | Partial<FieldArray<TFieldValues, TFieldArrayName>>
            | Array<Partial<FieldArray<TFieldValues, TFieldArrayName>>>,
        options?: FieldArrayMethodProps
    ) => void;
}

export const AddressInputFields: FC<IAddressInputFields> = ({
    addresses,
    onAddField,
    onRemoveField,
}) => {
    const addressesCount = addresses.length;

    const handleAddNewInput = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault(); // prevent for validation form when adding new input

        onAddField({
            address: '',
            city: '',
            state: '',
            zip: '',
        });
    };

    return (
        <>
            {addresses.map((address, index) => {
                return (
                    <Container key={address.address}>
                        <FormInput
                            id={`addresses.${index}.address`}
                            label="Street"
                            key={address.address}
                        />
                        <FormInput
                            id={`addresses.${index}.city`}
                            label="City"
                            key="city"
                        />
                        <FormInput
                            id={`addresses.${index}.state`}
                            label="State"
                            key={address.state}
                        />
                        <FormInput
                            id={`addresses.${index}.zip`}
                            label="Zip"
                            key={address.zip}
                        />

                        {addressesCount > MIN_FIELDS_LENGTH && (
                            <RemoveButton
                                type="button"
                                onClick={() => onRemoveField(index)}
                            >
                                Remove
                            </RemoveButton>
                        )}
                    </Container>
                );
            })}
            {addressesCount < MAX_FIELDS_LENGTH && (
                <AddButton onClick={handleAddNewInput}>Add address</AddButton>
            )}
        </>
    );
};
